import {
	type CaptureFileJSONObject,
	type DistanceBearingPitch,
	type SubFormSpanTypeState,
	SubFormType,
} from '@ikegps/core-api';
import { IkePoleForemanFieldIds, IPFSpanType, PoleFieldIds, SpanType } from './integrations';
import type {
	CollectionModelFieldSelectList,
	CollectionModelFieldSubFormInstance,
	CollectionModelFieldTrueSize,
	CollectionModelFieldVector,
	PLAForm,
} from '..';

/**
 * Standardized Span information.
 */
export interface ISpan {
	spanType: SpanType | null;

	spanVector: DistanceBearingPitch | null;

	spanTargetCollectionId: string | null;

	midSpanIkePhotos: CaptureFileJSONObject[];

	// wires: IWire[];
}

/** Normalize a raw span Type option value (IPF or standard style) to a SpanType, or null. */
export function normalizeSpanTypeValue(value: string | null | undefined): SpanType | null {
	switch (value) {
		case IPFSpanType.FORE_SPAN:
		case SpanType.NextPole:
			return SpanType.NextPole;
		case IPFSpanType.BACK_SPAN:
		case SpanType.PreviousPole:
			return SpanType.PreviousPole;
		case IPFSpanType.OTHER:
		case SpanType.Other:
			return SpanType.Other;
		default:
			return null;
	}
}

/**
 * The normalized span role of a subform instance. Uses the typed SPAN subform state when the
 * schema carries it, otherwise falls back to the legacy hardcoded Type field ids.
 */
export function getSpanRole(instance: CollectionModelFieldSubFormInstance): SpanType | null {
	if (instance.schema.subFormType === SubFormType.SPAN && instance.schema.subFormSpanTypeState) {
		return new SpanSubFormAdaptor(instance).spanType;
	}

	const typeField =
		instance.getOptionalField<CollectionModelFieldSelectList>(PoleFieldIds.SPAN.TYPE) ||
		instance.getOptionalField<CollectionModelFieldSelectList>(IkePoleForemanFieldIds.SPAN_TYPE);

	return normalizeSpanTypeValue(typeField?.value);
}

/**
 * SpanSubFormAdaptor wraps a SubForm instance whose SubForm type has been set to SPAN.
 * The SubForm instance is then mapped to the ISpan interface.
 */
export class SpanSubFormAdaptor implements ISpan {
	private subFormSpanTypeState: SubFormSpanTypeState;

	/**
	 * @throws The constructor throws an Error if the instance passed into the constructor is not for a Span SubForm.
	 * Be sure to check this condition before instantiating SpanSubFormAdaptor.
	 */
	constructor(public instance: CollectionModelFieldSubFormInstance) {
		if (instance.schema.subFormType !== SubFormType.SPAN) {
			throw new Error("SubForm instance's schema must have type SPAN to be passed into SpanSubFormAdaptor");
		}
		if (!instance.schema.subFormSpanTypeState) {
			throw new Error("SubForm instance's schema is type SPAN but is missing the SubFormSpanTypeState");
		}
		this.subFormSpanTypeState = instance.schema.subFormSpanTypeState;
	}

	public get spanVectorField(): CollectionModelFieldVector {
		return this.instance.getField<CollectionModelFieldVector>(this.subFormSpanTypeState.vectorFieldId);
	}

	public get spanTypeField(): CollectionModelFieldSelectList {
		return this.instance.getField<CollectionModelFieldSelectList>(this.subFormSpanTypeState.typeFieldId);
	}

	get spanType(): SpanType | null {
		return normalizeSpanTypeValue(this.spanTypeField.value);
	}

	get spanVector(): DistanceBearingPitch | null {
		return this.spanVectorField.value;
	}

	get spanTargetCollectionId(): string | null {
		if (!this.spanVectorField.isCollectionLink()) {
			return null;
		}
		return this.spanVectorField.stateValue?.collectionId ?? null;
	}

	public get spanMidSpanIkePhotoField(): CollectionModelFieldTrueSize | null {
		if (!this.subFormSpanTypeState.midSpanIkePhotoFieldId) {
			return null;
		}
		return this.instance.getField<CollectionModelFieldTrueSize>(this.subFormSpanTypeState.midSpanIkePhotoFieldId);
	}

	get midSpanIkePhotos(): CaptureFileJSONObject[] {
		return this.spanMidSpanIkePhotoField?.value ?? [];
	}
}

/**
 * PLASpanAdaptor wraps a SubForm instance from the Span field of any PLAForm compatible form.
 * The SubForm instance is then mapped to the ISpan interface.
 */
export class PLASpanAdaptor implements ISpan {
	/**
	 * @throws The constructor throws an Error if the SubForm instance passed into the
	 * constructor is not for the Span field of the given PLAForm.
	 * Be sure to check this condition before instantiating SpanSubFormAdaptor.
	 */
	constructor(
		public instance: CollectionModelFieldSubFormInstance,
		public form: PLAForm
	) {
		if (instance.schema.id !== form.spanField.id) {
			throw new Error("SubForm instance's schema must be the SPAN field of the given PLA form");
		}
	}

	public get spanTypeField(): CollectionModelFieldSelectList {
		return this.instance.getField(this.form.spanTypeField.id);
	}

	public get spanVectorField(): CollectionModelFieldVector {
		return this.instance.getField(this.form.spanVectorField.id);
	}

	get spanType(): SpanType | null {
		return normalizeSpanTypeValue(this.spanTypeField.value);
	}

	get spanVector(): DistanceBearingPitch | null {
		return this.spanVectorField.value;
	}

	get spanTargetCollectionId(): string | null {
		if (!this.spanVectorField.isCollectionLink()) {
			return null;
		}
		return this.spanVectorField.stateValue?.collectionId ?? null;
	}

	public get spanMidSpanIkePhotoField(): CollectionModelFieldTrueSize | null {
		const midSpanPhotoFormField = this.form.spanMidSpanIkePhoto;
		if (!midSpanPhotoFormField) {
			return null;
		}
		return this.instance.getField<CollectionModelFieldTrueSize>(midSpanPhotoFormField.id);
	}

	get midSpanIkePhotos(): CaptureFileJSONObject[] {
		return this.spanMidSpanIkePhotoField?.value ?? [];
	}
}
